<template>
    <div class="checkout-app">
        <component
            v-if="store.$state.mode && store.$state.mode !== 'unknown'"
            :is="getCheckoutComponent(store.$state.mode)"
        />
        <div v-else-if="store.$state.mode === 'unknown'">
            <h1 class="text-2xl font-bold text-center">Unknown Checkout Mode</h1>
            <p class="text-center">We're sorry, but we don't recognize the checkout mode you're trying to use.</p>
        </div>
        <AppLoading v-else />
    </div>
</template>

<script setup lang="ts">
import { watch, onBeforeUnmount, onMounted, defineAsyncComponent } from 'vue'
import { useCheckoutStore } from './store/checkout'
import { useCartStore } from './store/cart'
import AppLoading from './components/App/AppLoading.vue'
import { getPlan } from '@/utils/plans'
import { getById } from '@/utils/hardware'
import { getBundle } from '@/utils/bundles'
import debounce from 'lodash.debounce'

const GiftPurchaseCheckout = defineAsyncComponent(() => import('./components/Checkout/GiftPurchaseCheckout.vue'))
const GiftRedeemCheckout = defineAsyncComponent(() => import('./components/Checkout/GiftRedeemCheckout.vue'))
const HardwareCheckout = defineAsyncComponent(() => import('./components/Checkout/HardwareCheckout.vue'))
const SubscriptionCheckout = defineAsyncComponent(() => import('./components/Checkout/SubscriptionCheckout.vue'))
const OrgCheckout = defineAsyncComponent(() => import('./components/Checkout/OrgCheckout.vue'))
const UpgradeCheckout = defineAsyncComponent(() => import('./components/Checkout/UpgradeCheckout.vue'))
const BundleCheckout = defineAsyncComponent(() => import('./components/Checkout/BundleCheckout.vue'))
const TrialCheckout = defineAsyncComponent(() => import('./components/Checkout/TrialCheckout.vue'))

const getCheckoutComponent = (mode: string) => {
    switch (mode) {
        case 'equipment':
            return HardwareCheckout
        case 'gift':
            return GiftPurchaseCheckout
        case 'redeem':
            return GiftRedeemCheckout
        case 'subscription':
            return SubscriptionCheckout
        case 'org':
            return OrgCheckout
        case 'bundle':
            return BundleCheckout
        case 'upgrade':
            return UpgradeCheckout
        case 'trial':
            return TrialCheckout
        case 'unknown':
            return 'div'
        default:
            return SubscriptionCheckout
    }
}

const store = useCheckoutStore()
const cart = useCartStore()

const debouncedTaxUpdater = debounce((address: AddressFields) => {
    cart.updateTax(address)
}, 1200)

watch(
    () => store.taxableAddress?.postal_code,
    (newPostalCode, oldPostalCode) => {
        const address = store.taxableAddress
        if (!address?.postal_code) return
        if (cart.totals.pretax_total === 0) return
        if (newPostalCode !== oldPostalCode) {
            debouncedTaxUpdater(address)
        }
    },
)

watch(
    () => cart.totals.pretax_total,
    (pretax_total: number) => {
        if (pretax_total === 0) return
        store.taxableAddress?.postal_code && debouncedTaxUpdater(store.taxableAddress)
    },
)

onBeforeUnmount(() => {
    debouncedTaxUpdater.cancel()
})

const mountedFn = async() => {
    const params = new URLSearchParams(window.location.search)
    const locationPath = window.location.pathname
    const equipment_id: any = params.get('equipment_id')
    const qty: any = params.get('qty')
    const plan: keyof StripeKeyedPlan = params.get('plan') || 'standard'
    const billing: any = params.get('billing') || 'monthly'
    const bundleKey: any = params.get('bundle')

    // ----- Bundle Purchase -----
    if (bundleKey) {
        const bundle = getBundle(bundleKey)
        cart.$patch({
            plan: bundle?.plan?.stripe_price_id,
            planOptions: bundle?.plan?.stripe_price_id ? [bundle?.plan] : [],
            hardware: bundle?.equipment?.map((h) => h.stripe_price_id) || [],
            shipping: 'standard',
            bundle,
        })
        store.$patch({
            mode: 'bundle',
        })
        return
    }
    // ----- Hardware Purchase -----
    if (equipment_id) {
        const hardwareOption = getById(Number(equipment_id))
        if (hardwareOption && hardwareOption.price > 0) {
            cart.$patch({
                plan: undefined,
                shipping: 'standard',
            })
            cart.addHardware(hardwareOption.stripe_price_id)
            store.$patch({
                mode: 'equipment',
                selectedHardwareQty: Number(qty) || 1,
            })
            return
        }
    }

    // ----- Redeem -----
    if (locationPath.includes('/redeem')) {
        store.$patch({
            mode: 'redeem',
        })
        return
    }

    // ----- Org -----
    if (locationPath.includes('/org')) {
        let orgPath = locationPath
        if (orgPath.endsWith('/')) {
            orgPath = orgPath.slice(0, -1)
        }
        let orgKey = orgPath.split('/').pop()
        if (!orgKey) {
            store.$patch({
                mode: 'unknown',
            })
            return
        }
        const { getOrgByKey } = await import('@/utils/orgs')
        const org = getOrgByKey(orgKey)
        if (!org) {
            store.$patch({
                mode: 'unknown',
            })
            return
        }

        const selectedPlan = org?.plans?.[0]
        cart.$patch({
            plan: selectedPlan?.stripe_price_id,
            planOptions: org.plans || [],
            shipping: 'standard',
            hardware: selectedPlan?.hardware_options?.[0]?.stripe_price_id ? [selectedPlan?.hardware_options?.[0]?.stripe_price_id] : [],
        })
        if (org?.coupon) {
            const usePromoCodeModule = await import('@/composables/usePromoCode')
            const usePromoCode = usePromoCodeModule.default()
            usePromoCode.applyPromoCode(org.coupon).then(async(result: boolean) => {
                if (result) {
                    const { PROMO_CODE_TAX_ADDRESS } = await import('@/data/constants')
                    cart.updateTax(PROMO_CODE_TAX_ADDRESS)
                }
            })
        }

        store.$patch({
            mode: 'org',
            orgName: org.name,
        })

        return
    }

    // ----- Trial Purchase -----
    if (WR_CHECKOUT_GLOBALS?.flags?.isTrialPage) {
        const { selectedStripePlanId, selectedPlan } = getPlan('standard', 'monthly')
        store.$patch({
            mode: 'trial',
        })
        cart.$patch({
            plan: selectedStripePlanId,
            shipping: 'standard',
            planOptions: [selectedPlan],
        })
        cart.handlePlanChange()
        return
    }

    // ----- Subscription Purchase -----
    const { stripePlanOptions, selectedStripePlanId } = getPlan(plan, billing)

    if (params.get('gift')) {
        store.$patch({
            mode: 'gift',
        })
    } else if (params.get('upgrade')) {
        store.$patch({
            mode: 'upgrade',
        })
    } else {
        store.$patch({
            mode: 'subscription',
        })
    }

    if (selectedStripePlanId || stripePlanOptions.length) {
        watch(
            () => cart.plan,
            (plan) => {
                if (plan) {
                    cart.handlePlanChange()
                }
            },
        )
        cart.$patch({
            plan: selectedStripePlanId,
            shipping: 'standard',
            planOptions: stripePlanOptions,
        })
    }
}

onMounted(async() => {
    await mountedFn()
})
</script>

<style>
.checkout-app {
    position: relative;
    min-height: 80vh;
}

@media screen and (min-width: 768px) {
    .checkout-app {
        position: relative;
        min-height: 60vh;
    }
}

@media screen and (min-width: 1024px) {
    .checkout-app {
        position: relative;
        min-height: 50vh;
    }
}
</style>
